import React, {FC, useState, useEffect, MouseEvent, createRef} from "react"
import {Row, Col} from "@irati/ui/src/Layout"

import Flex from "@irati/ui/src/Flex"
import Spacing from "@irati/ui/src/Spacing"
import {OverlayPlacement, Tooltip} from "@irati/ui/src/Tooltip"
import {Body, H2, H4, Label, H1} from "@irati/ui/src/Typography"
import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {useTheme} from "@irati/ui/src/global/useTheme"
import adminTracking, {adminDashboardTracking} from "@irati-tracking/admin"
import Validation from "@irati/utils/validation"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import benefitsService from "@irati/benefits/services/benefits"
import usersService from "@irati/identity/services/users"
import Alert, {AlertTypes} from "@irati/ui/src/Alert"

import GenericModal from "@irati/ui/src/GenericModal"
import Button, {IconTypes} from "@irati/ui/src/Button"
import Form from "@irati/ui/src/Form"
import {Email, Firstname, Lastname} from "@irati-web/fields/user"
import authentication from "@irati/auth"
import {Input, TextArea, ValidationButton} from "@irati/ui/src/FormItem"
import Loading from "@irati/ui/src/Loading"
import Icon from "@irati/ui/src/Icon"
import assertionService from "@irati/auth/services/assertions"
import {GenerateAssertionProvider} from "@irati/types"
import SendInvitesModal from "../SendInvitesModal"
import {Wrapper, StyledImg, StyledCard, WidthCard, UsersCircle, Container} from "./styled"
import benefit, {PerkboxBenefit} from "./benefitMarketplace"
import NotificationPanel from "../NotificationPanel"
import devToggles from "@irati/dev-toggles"

interface Props extends InjectIntl {}

interface EnquiryData {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  businessName: string
  employeeCount: number
  message?: string
}

const Benefits: FC<Props> = ({formatMessage, intl}) => {
  const theme = useTheme()
  const [show, setShow] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [selectedBenefit, setSelectedBenefit] = useState<PerkboxBenefit | null>(null)
  const [enquiryData, setEnquiryData] = useState<EnquiryData>()
  const [success, setSuccess] = useState(false)
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [submit, setSubmit] = useState<Promise<any> | null>(null)
  const [unactivatedUsers, setUnactivatedUsers] = useState(0)
  const [copyAlert, setCopyAlert] = useState(false)
  const sendInvitesModal = createRef<any>()
  const [loadSSO, setLoadSSO] = useState<Promise<any>>()

  useEffect(() => {
    const employeeCountLoad = officesForTenant().then((offices) => {
      const ukTeamId = offices.find(({regionId}) => regionId === "UK")?.uuid
      return usersService
        .getUsersForBusiness({
          enabled: true,
          includeBusiness: false,
          includeIntegrations: false,
          totalOnly: true,
          teamId: ukTeamId
        })
        .then(({total}) => {
          const user = authentication.user()
          setEnquiryData({
            firstName: user?.firstName,
            lastName: user?.lastName,
            email: user?.email,
            phoneNumber: user?.phoneNumber,
            businessName: authentication.business().businessName,
            employeeCount: total,
            message: formatMessage({id: "admin.components.dashboard.benefits.enquiryForm.message"})
          })
        })
    })
    const unactivatedUsersLoad = usersService.getNonOnboardedUsersForBusiness({businessId: authentication.business().uuid}).then((res) => {
      setUnactivatedUsers(res?.total || 0)
    })
    setLoad(Promise.all([employeeCountLoad, unactivatedUsersLoad]))
  }, [])

  const handleClick = (clickedBenefit: PerkboxBenefit, event?: MouseEvent) => {
    if (event) {
      event.stopPropagation()
    }
    const action = clickedBenefit?.action || "cta_learnMore"
    adminDashboardTracking.adminBenefits(clickedBenefit.subproduct, action)
    window.open(formatMessage({id: clickedBenefit.link}), "_blank")
    return false
  }

  const handleSendEnquiry = () => {
    adminDashboardTracking.adminBenefitsEnquiry(selectedBenefit.subproduct)
    const data = {
      benefitId: selectedBenefit?.benefitId,
      employees: enquiryData?.employeeCount,
      contact: {
        email: enquiryData?.email,
        firstName: enquiryData?.firstName,
        lastName: enquiryData?.lastName,
        telephone: enquiryData?.phoneNumber
      },
      customerMessage: enquiryData?.message
    }
    setSubmit(benefitsService.createEnquiry({data}).then(() => setSuccess(true)))
  }

  const handleReturn = () => {
    setShow(true)
    setShowConfirm(false)
  }

  const handleClose = () => {
    setShow(false)
    if (!success) {
      setShowConfirm(true)
    }
  }

  const handleCloseConfirm = () => {
    setShowConfirm(false)
  }

  const handleOpenModal = () => {
    adminDashboardTracking.adminDashboardOpenSendInviteModule()
    sendInvitesModal.current.show()
  }

  const handleCopyLink = () => {
    const link = `${window.location.origin}/activate?utm_source=admin_activation_invite&utm_medium=link`
    const copyText = formatMessage({id: "admin.components.dashboard.adminNotification.copyText"}, {link})
    navigator.clipboard.writeText(copyText).then(() => {
      setCopyAlert(true)
    })
    adminTracking.ctaAdminActivationInvite()
  }
  const {text, linkText} = benefit
  const onClick = () => handleClick(benefit)

  const handleGenerateAssertion = () => {
    adminDashboardTracking.adminBenefits("manage_orders", "ctaViewManagerOrders")

    const promise = assertionService.generateAssertion({
      provider: GenerateAssertionProvider.ServiceProviderVivup
    })
    setLoadSSO(promise)
    promise.then(({response, acs}) => {
      const form = document.createElement("form")
      form.method = "POST"
      form.action = acs
      form.target = "_blank"
      const responseInput = document.createElement("input")
      responseInput.type = "hidden"
      responseInput.name = "SAMLResponse"
      responseInput.value = response
      form.appendChild(responseInput)
      document.body.appendChild(form)
      form.submit()
      document.body.removeChild(form)
    })
  }

  return (
    <>
      <Wrapper>
        {devToggles.showAdminNotificationPanel() && <NotificationPanel />}
        <H2 marginBottom={3}>
          <FormattedMessage id="admin.components.dashboard.adminNotification.title" />
        </H2>
        {copyAlert && (
          <Alert
            type={AlertTypes.success}
            title="admin.components.dashboard.adminNotification.copyAlert"
            onHide={() => setCopyAlert(false)}
          />
        )}
        <Container justifyContent="space-between" gap={1}>
          {unactivatedUsers > 0 && (
            <WidthCard bgColor={theme.colors.greys.lightest}>
              <Flex gap={2} alignItems="center">
                <UsersCircle>
                  <Flex direction="column" justifyContent="center" alignItems="center" height="100%">
                    <H1 color={theme.colors.success}>{intl.formatNumber(unactivatedUsers)}</H1>
                    <Label textAlign="center">
                      <FormattedMessage id="admin.components.dashboard.unactivated.label" />
                    </Label>
                  </Flex>
                </UsersCircle>
                <Flex direction="column">
                  <H2>
                    <FormattedMessage id="admin.components.dashboard.unactivated.title2" />
                  </H2>
                  <Body small marginTop marginBottom={2}>
                    <FormattedMessage id="admin.components.dashboard.activated.text2" />
                  </Body>
                  <Flex justifyContent="flex-start" gap>
                    <Button small primary onClick={handleCopyLink}>
                      <FormattedMessage id="admin.components.dashboard.activated.button3" />
                    </Button>
                    <Button small onClick={handleOpenModal}>
                      <FormattedMessage id="admin.components.dashboard.activated.button2" />
                    </Button>
                  </Flex>
                </Flex>
              </Flex>
            </WidthCard>
          )}
          {authentication.apps().benefits ? (
            <WidthCard bgColor={theme.colors.greys.lightest}>
              <Flex direction="column">
                <H2>
                  <FormattedMessage id="admin.components.dashboard.benefits.manageOrderRequest.title" />
                </H2>
                <Body small marginTop={2} marginBottom={2}>
                  <FormattedMessage id="admin.components.dashboard.benefits.manageOrderRequest.subTitle" />
                </Body>
                <Flex>
                  <Button small onClick={handleGenerateAssertion} load={loadSSO}>
                    <FormattedMessage id="admin.components.dashboard.benefits.manageOrderRequest.cta.title" />
                    <Icon icon={IconTypes.upRightArrow} marginLeft={2} size={16} />
                  </Button>
                </Flex>
              </Flex>
            </WidthCard>
          ) : (
            <StyledCard bgImg={formatMessage({id: benefit.bgImg})} key="perkboxBenefitMarketplace" onClick={onClick}>
              <Loading load={load}>
                <Flex direction="column" justifyContent="space-between" minHeight="100%">
                  <Flex gap={1} marginBottom={1}>
                    <StyledImg src={formatMessage({id: benefit.img})} width={64} height={64} alt="" />
                    <div>
                      <Flex gap={0.5} marginBottom={2}>
                        <Flex>
                          {benefit.icons.map((icon, index) => (
                            <div key={`perkboxBenefit-${index}`}>
                              <Spacing marginLeft={index === 0 ? 0 : -0.5} key={`perkboxBenefitIcon-${index}`}>
                                <Tooltip placement={OverlayPlacement.Top} tooltipChildren={formatMessage({id: icon.text})}>
                                  <img src={formatMessage({id: icon.img})} width={24} height={24} alt="" />
                                </Tooltip>
                              </Spacing>
                            </div>
                          ))}
                        </Flex>
                      </Flex>
                      <Flex gap={0.5}>
                        <H4 color={theme.colors.greys.white}>
                          <FormattedMessage id={text} />
                        </H4>
                      </Flex>
                    </div>
                  </Flex>
                  <Flex justifyContent="flex-end">
                    <Button primary inverted onClick={(event: MouseEvent) => handleClick(benefit, event)}>
                      <FormattedMessage id={linkText} />
                    </Button>
                  </Flex>
                </Flex>
              </Loading>
            </StyledCard>
          )}
        </Container>
      </Wrapper>
      <GenericModal hideActions show={show} onHide={handleClose}>
        {success ? (
          <>
            <H2 marginBottom={1}>
              <FormattedMessage id="admin.components.dashboard.benefits.enquiryForm.success.title" />
            </H2>
            <Body small marginBottom={1}>
              <FormattedMessage id="admin.components.dashboard.benefits.enquiryForm.success.text" />
            </Body>
            <Flex justifyContent="center">
              <img src={formatMessage({id: "admin.success.img"})} width={150} height={150} />
            </Flex>
          </>
        ) : (
          <>
            <H2 marginBottom={1}>
              <FormattedMessage id="admin.components.dashboard.benefits.enquiryForm.title" />
            </H2>
            <Body small marginBottom={1}>
              <FormattedMessage id="admin.components.dashboard.benefits.enquiryForm.text" />
            </Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Input
                    label={formatMessage({id: "admin.components.dashboard.benefits.enquiryForm.labels.companyName"})}
                    value={enquiryData?.businessName}
                    onChange={(value: string) => setEnquiryData((prevState) => ({...prevState, businessName: value}))}
                    validation={Validation.string("platform.defaultValidation.required")}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    label={formatMessage({id: "admin.components.dashboard.benefits.enquiryForm.labels.employeeCount"})}
                    value={enquiryData?.employeeCount}
                    onChange={(value: string) => setEnquiryData((prevState) => ({...prevState, employeeCount: value}))}
                    validation={Validation.number("platform.defaultValidation.required").min(1)}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Firstname
                    value={enquiryData?.firstName}
                    onChange={(value: string) => setEnquiryData((prevState) => ({...prevState, firstName: value}))}
                  />
                </Col>
                <Col md={6}>
                  <Lastname
                    value={enquiryData?.lastName}
                    onChange={(value: string) => setEnquiryData((prevState) => ({...prevState, lastName: value}))}
                  />
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Email
                    value={enquiryData?.email}
                    onChange={(value: string) => setEnquiryData((prevState) => ({...prevState, email: value}))}
                  />
                </Col>
                <Col md={6}>
                  <Input
                    label={formatMessage({id: "admin.components.dashboard.benefits.enquiryForm.labels.phone"})}
                    value={enquiryData?.phoneNumber}
                    onChange={(value: string) => setEnquiryData((prevState) => ({...prevState, phoneNumber: value}))}
                    validation={Validation.string("platform.defaultValidation.required")}
                  />
                </Col>
              </Row>
              <TextArea
                name="benefitEnquiryMessage"
                label={formatMessage({id: "admin.components.dashboard.benefits.enquiryForm.labels.message"})}
                rows={5}
                value={enquiryData?.message}
                onChange={(value: string) => setEnquiryData((prevState) => ({...prevState, message: value}))}
              />
              <Loading load={submit} useLoader={false} />
              <Flex justifyContent="flex-end">
                <ValidationButton primary onClick={handleSendEnquiry}>
                  <FormattedMessage id="admin.components.dashboard.benefits.enquiryForm.button.submit" />
                </ValidationButton>
              </Flex>
            </Form>
          </>
        )}
      </GenericModal>
      <GenericModal hideActions show={showConfirm} onHide={handleCloseConfirm}>
        <H2 marginBottom>
          <FormattedMessage id="admin.components.dashboard.benefits.enquiryForm.abandonModal.title" />
        </H2>
        <Body marginBottom={3} maxWidth="100%">
          <FormattedMessage id="admin.components.dashboard.benefits.enquiryForm.abandonModal.subTitle" />
        </Body>
        <Flex alignItems="center" justifyContent="flex-end" gap={5}>
          <Button primary onClick={handleReturn}>
            <FormattedMessage id="admin.components.dashboard.benefits.enquiryForm.abandonModal.returnBtn" />
          </Button>
          <Button tertiary onClick={handleCloseConfirm} marginRight={5}>
            <FormattedMessage id="admin.components.dashboard.benefits.enquiryForm.abandonModal.closeBtn" />
          </Button>
        </Flex>
      </GenericModal>
      <SendInvitesModal ref={sendInvitesModal} />
    </>
  )
}

export default injectIntl(Benefits)
